import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { Alert, Snackbar } from '@mui/material';

/**
 * App-wide toast. Replaces the browser's blocking `alert('Setting updated successfully!')` popups,
 * which interrupt the admin, cannot be styled, and say nothing about WHAT failed.
 *
 *   const notify = useNotify();
 *   notify.success('Saved');
 *   notify.error(err);              // pulls the server's message out of an axios error
 */

type Severity = 'success' | 'error' | 'info' | 'warning';
interface Toast { id: number; severity: Severity; message: string }

interface NotifyApi {
    success: (message: string) => void;
    info: (message: string) => void;
    warning: (message: string) => void;
    /** Accepts a string or any thrown value; shows the API's `error` text when there is one. */
    error: (errOrMessage: unknown, fallback?: string) => void;
}

const NotifyContext = createContext<NotifyApi | null>(null);

/** The most useful sentence we can find on a failed request. */
export function errorMessage(err: unknown, fallback = 'Something went wrong. Please try again.'): string {
    if (typeof err === 'string') return err;
    const e = err as any;
    return e?.response?.data?.error
        || e?.response?.data?.message
        || (e?.code === 'ERR_NETWORK' ? 'Cannot reach the server. Check your connection.' : null)
        || e?.message
        || fallback;
}

export const NotifierProvider = ({ children }: { children: React.ReactNode }) => {
    const [toast, setToast] = useState<Toast | null>(null);

    const show = useCallback((severity: Severity, message: string) => {
        setToast({ id: Date.now() + Math.random(), severity, message });
    }, []);

    const api = useMemo<NotifyApi>(() => ({
        success: (m) => show('success', m),
        info: (m) => show('info', m),
        warning: (m) => show('warning', m),
        error: (e, fallback) => show('error', errorMessage(e, fallback)),
    }), [show]);

    return (
        <NotifyContext.Provider value={api}>
            {children}
            <Snackbar
                key={toast?.id}
                open={!!toast}
                autoHideDuration={toast?.severity === 'error' ? 7000 : 3500}
                onClose={(_e, reason) => { if (reason !== 'clickaway') setToast(null); }}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                {toast ? (
                    <Alert severity={toast.severity} variant="filled" onClose={() => setToast(null)} sx={{ minWidth: 280, maxWidth: 520 }}>
                        {toast.message}
                    </Alert>
                ) : undefined}
            </Snackbar>
        </NotifyContext.Provider>
    );
};

export const useNotify = (): NotifyApi => {
    const ctx = useContext(NotifyContext);
    if (!ctx) throw new Error('useNotify must be used inside <NotifierProvider>');
    return ctx;
};
